import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';

const whatsAppSessionSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  session_name: z.string(),
  status: z.string().nullable(),
  phone_number: z.string().nullable(),
  last_connected_at: z.string().nullable(),
  created_at: z.string().nullable(),
});

export type WhatsAppSession = z.infer<typeof whatsAppSessionSchema>;

async function fetchWhatsAppSession(): Promise<WhatsAppSession | null> {
  const { data: auth } = await supabase.auth.getUser();
  const user = auth.user;
  if (!user) return null;

  const { data, error } = await supabase
    .from('personal_whatsapp_sessions')
    .select('*')
    .eq('user_id', user.id)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const parsed = whatsAppSessionSchema.safeParse(data);
  if (!parsed.success) {
    console.error('Invalid WhatsApp session payload:', parsed.error.flatten());
    throw new Error('Dados inválidos da sessão do WhatsApp');
  }

  return parsed.data;
}

function getConnectedDuration(lastConnectedAt: string | null): string | null {
  if (!lastConnectedAt) return null;

  const connectedAt = new Date(lastConnectedAt);
  const now = new Date();
  const diffMs = now.getTime() - connectedAt.getTime();

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  if (days > 0) return `${days} dia${days > 1 ? 's' : ''}`;
  if (hours > 0) return `${hours} hora${hours > 1 ? 's' : ''}`;
  return 'Agora mesmo';
}

export function useWhatsAppSession() {
  const query = useQuery({
    queryKey: ['whatsappSession'],
    queryFn: fetchWhatsAppSession,
    staleTime: 1000 * 15,
    gcTime: 1000 * 60 * 10,
  });

  const session = query.data ?? null;

  const derived = useMemo(() => {
    const isConnected = session?.status === 'connected';
    return {
      isConnected,
      phoneNumber: session?.phone_number ?? null,
      connectedDuration: getConnectedDuration(session?.last_connected_at ?? null),
    };
  }, [session]);

  return {
    session,
    loading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
    isConnected: derived.isConnected,
    phoneNumber: derived.phoneNumber,
    connectedDuration: derived.connectedDuration,
    refetch: query.refetch,
  };
}
